/**
 * Search (07 Workshop, rows 16 and 55): ⌘K or Search in the sidebar. One
 * field; results group by kind, outputs (documents and ideas), projects, then
 * chats, newest first in each. A result opens where it lives.
 */
import { useState } from "react";
import { PanelRight, Search, X } from "lucide-react";
import { KitModal } from "@/app/routes/dashboard/components/kit/Modal";
import { useWorkshopSearch, type WorkshopSearchHit } from "../api/useWorkshopSearch";
import { documentTag, type WorkshopOutput } from "../workshopOutputs";
import { tileDate } from "./WorkshopProjectsPage";
import { useWorkshopDesk } from "./workshopDesk";
import { KindTag } from "./WorkshopParts";

type Props = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  isPhone: boolean;
  /** Open an output where it lives, in its project's panel. */
  onOpenOutput: (output: WorkshopOutput) => void;
};

function asOutput(hit: WorkshopSearchHit): WorkshopOutput {
  const kind = hit.type === "idea" ? "idea" : "document";
  return {
    key: `${kind}:${hit.id}`,
    kind,
    id: hit.id,
    projectId: hit.projectId,
    title: hit.title,
    tag: kind === "idea" ? "IDEA" : documentTag(hit.kind ?? ""),
    version: null,
    state: null,
    madeAt: hit.updatedAt,
    at: hit.updatedAt,
    thumbUrl: null,
  };
}

export default function WorkshopSearchDialog({ isOpen, onOpenChange, isPhone, onOpenOutput }: Props) {
  const desk = useWorkshopDesk();
  const [q, setQ] = useState("");
  const search = useWorkshopSearch(isOpen ? desk.brandId : null, q);
  const hits = search.data ?? [];
  const term = q.trim();
  const outputs = hits.filter((h) => h.type === "output" || h.type === "idea");
  const projects = hits.filter((h) => h.type === "project");
  const chats = hits.filter((h) => h.type === "chat");

  const firstGroup =
    outputs.length > 0
      ? { label: "Outputs", count: outputs.length }
      : projects.length > 0
        ? { label: "Projects", count: projects.length }
        : { label: "Chats", count: chats.length };

  const close = () => {
    onOpenChange(false);
    setQ("");
  };

  return (
    <KitModal
      isOpen={isOpen}
      onOpenChange={(open) => (open ? onOpenChange(true) : close())}
      title="Search"
      modalClassName="ws-dialog ws-dialog--form ws-search-dialog"
      overlayClassName="ws-dialog-overlay ws-search-overlay"
      pinTop={!isPhone}
      data-phone={isPhone || undefined}
    >
      <span className="ws-sheet-grabber" aria-hidden="true" />
      <div className="ws-search">
        <label className="ws-search-field">
          <Search className="ws-search-glyph" aria-hidden="true" />
          <input
            autoFocus
            className="co-input-own-focus"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="SEARCH"
            aria-label="Search projects, outputs and chats"
          />
          {q ? (
            <button type="button" className="ws-side-icon" aria-label="Clear" onClick={() => setQ("")}>
              <X aria-hidden="true" />
            </button>
          ) : null}
        </label>

        {term.length < 2 ? (
          <p className="ws-search-hint">Projects, outputs and chats in {desk.brandName ?? "this brand"}.</p>
        ) : search.isLoading ? (
          <p className="ws-search-hint" role="status">
            Searching…
          </p>
        ) : search.isError ? (
          <p className="ws-search-hint" role="alert">
            Search did not answer. Try again in a moment.
          </p>
        ) : hits.length === 0 ? (
          <p className="ws-search-hint">Nothing matches “{term}”.</p>
        ) : (
          <>
          {/* The first group's label sits above the scroller, so the scrollbar
              starts at the results. Later groups' labels scroll with them. */}
          <GroupHead label={firstGroup.label} count={firstGroup.count} />
          <div className="ws-search-results">
            {outputs.length > 0 ? (
              <Group label="Outputs" count={outputs.length} hideHead={firstGroup.label === "Outputs"}>
                {outputs.map((hit) => {
                  const output = asOutput(hit);
                  const open = () => {
                    close();
                    onOpenOutput(output);
                  };
                  return (
                    <li key={hit.type + hit.id} className="ws-oitem ws-oitem--search">
                      <button type="button" className="ws-oitem-open" onClick={open} aria-label={`Open ${hit.title}`} />
                      <DocMark />
                      <span className="ws-oitem-text">
                        <span className="ws-oitem-head">
                          <span className="ws-oitem-tags">
                            {output.tag ? <KindTag>{output.tag}</KindTag> : null}
                            <span className="ws-oitem-meta">{tileDate(hit.updatedAt)}</span>
                          </span>
                          <span className="ws-oitem-actions">
                            <button type="button" className="ws-side-icon" aria-label="Open beside" onClick={open}>
                              <PanelRight aria-hidden="true" />
                            </button>
                          </span>
                        </span>
                        <span className="ws-oitem-title">{hit.title}</span>
                        <span className="ws-oitem-project">{hit.projectName}</span>
                      </span>
                    </li>
                  );
                })}
              </Group>
            ) : null}
            {projects.length > 0 ? (
              <Group label="Projects" count={projects.length} hideHead={firstGroup.label === "Projects"}>
                {projects.map((hit) => (
                  <li key={hit.id}>
                    <button
                      type="button"
                      className="ws-search-line"
                      onClick={() => {
                        close();
                        desk.openProject(hit.id);
                      }}
                    >
                      {hit.title}
                      {hit.archived ? <span className="ws-oitem-meta"> · ARCHIVED</span> : null}
                    </button>
                  </li>
                ))}
              </Group>
            ) : null}
            {chats.length > 0 ? (
              <Group label="Chats" count={chats.length} hideHead={firstGroup.label === "Chats"}>
                {chats.map((hit) => (
                  <li key={hit.id}>
                    <button
                      type="button"
                      className="ws-search-line"
                      onClick={() => {
                        close();
                        desk.openThread(hit.projectId, hit.id);
                      }}
                    >
                      {hit.title || "New chat"} · {hit.projectName}
                    </button>
                  </li>
                ))}
              </Group>
            ) : null}
          </div>
          </>
        )}
      </div>
    </KitModal>
  );
}

function GroupHead({ label, count }: { label: string; count: number }) {
  return (
    <div className="ws-section-head">
      <span className="ws-section-label">{label}</span>
      <span className="ws-section-count">{count}</span>
    </div>
  );
}

function Group({
  label,
  count,
  hideHead = false,
  children,
}: {
  label: string;
  count: number;
  hideHead?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section className="ws-search-group" aria-label={label}>
      {hideHead ? null : <GroupHead label={label} count={count} />}
      <ul className="ws-search-list">{children}</ul>
    </section>
  );
}

/** A document's mark: faint lines on a small page. */
export function DocMark() {
  return (
    <span className="ws-doc-mark" aria-hidden="true">
      <span style={{ width: "92%" }} />
      <span style={{ width: "100%" }} />
      <span style={{ width: "74%" }} />
      <span style={{ width: "96%" }} />
      <span style={{ width: "68%" }} />
      <span style={{ width: "84%" }} />
    </span>
  );
}
